import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Card, CARD_STATUS_MAP } from '../../interfaces/cards.interface';
import { PersonsService } from '../../services/persons.service';
import { BehaviorSubject, Subject, take, takeUntil } from 'rxjs';
import { Children, EditPerson, Person } from '../../interfaces/persons.interface';
import { CardsService } from '../../services/cards.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { DialogConfirmComponent } from '../../shared/dialog-confirm/dialog-confirm.component';
import { MessageInfoComponent } from './create-message-info/create-message-info.component';
import { BranchSelectionComponent } from '../../shared/branch-selection/branch-selection.component';
import { environment } from '../../../environments/environment';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CamapignScanService } from '../../services/campaign-scan.service';
import { CardStatisticsResponse } from '../../interfaces/campaigns.interface';
import { Branch } from '../../interfaces/branch.interface';
import { BranchService } from '../../services/branch.service';


@Component({
  selector: 'list-app-cards',
  standalone: false,
  templateUrl: './cards.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./cards.component.scss']
})
export class ListCardsComponent implements OnInit {
  private $destroy = new Subject<void>();
  cards: Card[] = [];

  cardStats = new Map<number, CardStatisticsResponse>();
  @Input() person!: Person; // Parent person object
  @Output() personChanged = new EventEmitter<void>();
  status_map = CARD_STATUS_MAP; // Map for card statuses
  seniorForm!: FormGroup;
  submitting = false;
  branch_id: number | null = null;

  constructor(
    public dialog: MatDialog,
    public personService: PersonsService,
    public cardSevice: CardsService,
    public snackBar: MatSnackBar,
    private formBuilder: FormBuilder,
    private campaignScanService: CamapignScanService,
    private branchService: BranchService
  ) {
    // Initialize cards if needed
    this.cards = [];
    this.initializeSeniorForm();
  }

  get statusKeys() {
    return Object.keys(CARD_STATUS_MAP).map(Number);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['person'] && changes['person'].currentValue) {
      this.cards = (this.person.cards || []).sort((a, b) => a.status - b.status);
      this.initializeSeniorForm();
      this.loadCardStats();
    }
  }

  loadCardStats(): void {
    for (const card of this.cards) {
      if (card.id > 0) {
        this.campaignScanService.getCardStatistics({ card_id: card.id }).subscribe({
          next: (res) => this.cardStats.set(card.id, res),
          error: () => {}
        });
      }
    }
  }

  initializeSeniorForm(): void {
    this.seniorForm = this.formBuilder.group({
      complementary: [
        this.person?.complementary || '',
        [Validators.required, Validators.minLength(3), Validators.pattern(/^[0-9,\s]+$/)]
      ]
    });
  }


  ngOnInit(): void {
    this.loadCardStats();
  }

  trackByChildId(index: number, child: Children): number {
    return child.id;
  }

  hasCardFor(id: number): boolean {
    return this.cards && this.cards.some(c => c.person_id === id && c.status < 8);
  }

  ngOnDestroy(): void {
    this.$destroy.next();
    this.$destroy.complete();
  }

  onSubmit(): void {
    if (this.seniorForm.invalid || this.submitting) {
      return;
    }

    this.submitting = true;
    const complementaryValue = this.seniorForm.get('complementary')?.value?.trim();

    if (!complementaryValue) {
      this.submitting = false;
      return;
    }

    const updateData = {
      complementary: complementaryValue
    };

    this.personService.updatePerson(this.person.id, updateData as EditPerson).subscribe({
      next: (updatedPerson: Person) => {
        //   this.person = updatedPerson;
        this.cards = (updatedPerson.cards || []).sort((a, b) => a.status - b.status);
        this.seniorForm.patchValue({ complementary: updatedPerson.complementary });
        this.submitting = false;

        this.personService.getCurrent().subscribe(data => {
          this.personService.current$.next(data);
          // Find the updated person in the array or use the first one
          const updatedPerson = data.find(p => p.id === this.person.id) || data[0];
          if (updatedPerson) {
            this.person = updatedPerson;
            this.cards = (updatedPerson.cards || []).sort((a, b) => a.status - b.status);

          }

          //this.personChanged.emit();


          this.snackBar.open('Karty wnuków zostały pomyślnie powiązane.', 'Zamknij', {
            duration: 7000,
            panelClass: ['success-snackbar'],
            horizontalPosition: 'center',
            verticalPosition: 'bottom'
          });

          // Emit event to notify parent component
        });



        // this.personChanged.emit();
      },
      error: (err: any) => {
        this.submitting = false;
        const errorMessage = err.error?.detail || err.error?.message || 'Błąd podczas aktualizacji karty';

        this.snackBar.open(errorMessage, 'Zamknij', {
          duration: 9000,
          panelClass: ['error-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom'
        });
      }
    });
  }

  orderCard(person: Person | Children): void {
    const pageIds = environment.cardOrderPageIds || {
      DEFAULT: 'karta_dla_rodzica',
      CITIZEN: 'karta_dla_placacego_podatk',
      SENIOR: 'karta_dla_seniora',
      STUDENT: 'karta_dla_studenta'
    };

    let pageId = pageIds.DEFAULT;
    switch (this.person.type) {
      case 'CITIZEN':
        pageId = pageIds.CITIZEN;
        break;
      case 'SENIOR':
        pageId = pageIds.SENIOR;
        break;
      case 'STUDENT':
        pageId = pageIds.STUDENT;
        break;
      default:
        pageId = pageIds.DEFAULT;
    }

    // First, open branch selection dialog
    const branchDialogRef = this.dialog.open(BranchSelectionComponent, {
      minWidth: '200px',
      maxWidth: '500px',
      width: '80vw',
      minHeight: '300px'
      
    });

    branchDialogRef.afterClosed().subscribe(branchId => {
      if (!branchId) {
        // User cancelled branch selection
        return;
      }

      // Store selected branch_id
      this.branch_id = branchId;

      // Now open the confirmation dialog
      const dialogRef = this.dialog.open(DialogConfirmComponent, {
        width: '400px',
        maxHeight: '90vh',
        data: {
          title: 'Potwierdzenie zamówienia karty',
          messageComponent: MessageInfoComponent,
          pageId: pageId
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === 'ok') {
          this.cardSevice.orderCardForUser({ person_id: person.id, layout_id: 0, branch_id: this.branch_id }).subscribe({
            next: (card) => {
             
              this.snackBar.open('Karta została zarejestorwana. Odbiór znajduje sie w wybranym punkcie Zgrane Grono po pokazaniu dokumentów uprawniających do wydania karty.', 'Zamknij', {
                duration: 30000,
                panelClass: ['success-snackbar'],
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
              });

              this.personService.getCurrent(true).subscribe(data => {
                this.personService.current$.next(data);

                const updatedPerson = data.find(p => p.id === person.id) || data[0];
                if (updatedPerson) {

                  this.person = updatedPerson;
                  this.cards = (updatedPerson.cards || []).sort((a, b) => a.status - b.status);
                  this.initializeSeniorForm();
                  this.personChanged.emit();
                }
              });
            },
            error: (err: any) => {
              let errorMessage = err.error?.detail || 'An error occurred';

              if (errorMessage === 'Calculated valid_until date is in the past.') {
                errorMessage = 'Wiek posiadacza karty jest niezgodny z wybranym typem karty. Popraw datę albo skontaktuj się z obsługą serwisu.';
              }

              this.snackBar.open(errorMessage, 'Zamknij', {
                duration: 20000,
                panelClass: ['error-snackbar'],
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
              });
            },
          });
        }
      });
    }); // Close branchDialogRef.afterClosed()
  }

}